import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePendingChangesStore from '../../stores/pendingChangesStore';
import useFileStore from '../../stores/fileStore';
import convex from '../../convexClient.js';
import { api } from '../../../convex/_generated/api';
import './DiffView.css';

// Simple diff algorithm - finds added and removed lines
function computeDiff(original, modified) {
    const originalLines = (original || '').split('\n');
    const modifiedLines = (modified || '').split('\n');

    const result = [];
    let i = 0, j = 0;

    while (i < originalLines.length || j < modifiedLines.length) {
        if (i >= originalLines.length) {
            // Remaining lines are additions
            result.push({ type: 'added', content: modifiedLines[j], lineNumber: j + 1 });
            j++;
        } else if (j >= modifiedLines.length) {
            // Remaining lines are deletions
            result.push({ type: 'removed', content: originalLines[i], lineNumber: i + 1 });
            i++;
        } else if (originalLines[i] === modifiedLines[j]) {
            // Lines match
            result.push({ type: 'unchanged', content: originalLines[i], lineNumber: j + 1 });
            i++;
            j++;
        } else {
            // Check if this is a modification or addition/deletion
            const nextMatchInModified = modifiedLines.slice(j + 1).indexOf(originalLines[i]);
            const nextMatchInOriginal = originalLines.slice(i + 1).indexOf(modifiedLines[j]);

            if (nextMatchInModified !== -1 && (nextMatchInOriginal === -1 || nextMatchInModified <= nextMatchInOriginal)) {
                // Addition
                result.push({ type: 'added', content: modifiedLines[j], lineNumber: j + 1 });
                j++;
            } else if (nextMatchInOriginal !== -1) {
                // Deletion
                result.push({ type: 'removed', content: originalLines[i], lineNumber: i + 1 });
                i++;
            } else {
                // Modification (show as remove + add)
                result.push({ type: 'removed', content: originalLines[i], lineNumber: i + 1 });
                result.push({ type: 'added', content: modifiedLines[j], lineNumber: j + 1 });
                i++;
                j++;
            }
        }
    }

    return result;
}

function DiffView({ projectId, filePath }) {
    const { t } = useTranslation();
    const { getPendingChange, acceptChange, rejectChange } = usePendingChangesStore();
    const { loadFiles, closeTab } = useFileStore();

    const pendingChange = getPendingChange(filePath);

    const diffLines = useMemo(() => {
        if (!pendingChange) return [];
        return computeDiff(pendingChange.originalContent, pendingChange.newContent);
    }, [pendingChange]);

    if (!pendingChange) {
        return null;
    }

    const handleAccept = async () => {
        const change = pendingChange;
        const newContent = acceptChange(filePath);
        if (newContent !== null) {
            if (change?.isNewFile) {
                const parentPath = typeof change.parentPath === 'string'
                    ? change.parentPath
                    : filePath.includes('/')
                        ? filePath.substring(0, filePath.lastIndexOf('/'))
                        : '';
                const name = typeof change.name === 'string' && change.name.trim()
                    ? change.name
                    : filePath.includes('/')
                        ? filePath.substring(filePath.lastIndexOf('/') + 1)
                        : filePath;

                await convex.mutation(api.files.upsert, {
                    projectId,
                    path: filePath,
                    name,
                    type: 'file',
                    parentPath,
                    content: newContent
                });
            } else {
                await convex.mutation(api.files.updateContent, {
                    projectId,
                    path: filePath,
                    content: newContent
                });
            }

            // Reload files to update UI
            await loadFiles(projectId);
        }
    };

    const handleReject = () => {
        const wasNewFile = pendingChange?.isNewFile;
        rejectChange(filePath);
        if (wasNewFile) {
            closeTab(filePath);
        }
    };

    const addedCount = diffLines.filter(l => l.type === 'added').length;
    const removedCount = diffLines.filter(l => l.type === 'removed').length;

    return (
        <div className="diff-view">
            {/* Header */}
            <div className="diff-header">
                <div className="diff-info">
                    <span className="diff-title">✨ {t('editor.aiSuggestedChanges')}</span>
                    <span className="diff-stats">
                        <span className="stat-added">+{addedCount}</span>
                        <span className="stat-removed">-{removedCount}</span>
                    </span>
                </div>
                <div className="diff-description">
                    {pendingChange.description || t('editor.reviewChangesBelow')}
                </div>
                <div className="diff-actions">
                    <button className="btn btn-ghost reject-btn" onClick={handleReject}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        {t('editor.reject')}
                    </button>
                    <button className="btn btn-primary accept-btn" onClick={handleAccept}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {t('editor.acceptChanges')}
                    </button>
                </div>
            </div>

            {/* Diff content */}
            <div className="diff-content">
                {diffLines.map((line, idx) => (
                    <div key={idx} className={`diff-line ${line.type}`}>
                        <span className="line-indicator">
                            {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                        </span>
                        <span className="line-content">{line.content || ' '}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default DiffView;
